import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table"
import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useAtom } from "jotai"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import api from "../services/api"
import { useParams } from "react-router-dom"
import AdminLayout from "../Components/Layout/AdminLayout"
import AssignEncircledAdminModal from "../Components/Modals/AssignEncircledAdminModal"
import { userAtom } from "../state"
import UserFormModal from "../Components/Modals/UserFormModal"
import { ManageUserOrganizationsModal } from "../Components/ManageUserOrganizationsModal"
import { AccessStatusBadge } from "../Components/Admin/AccessStatusBadge"
import { canSendInvitation, getAccessStatus } from "../constants/accessStatus"

const col = createColumnHelper()

const Users = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sorting, setSorting] = useState([{ id: "name", desc: false }])
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 })
  const [pageCount, setPageCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [allTotals, setAllTotals] = useState({ plansCreated: 0, plansCompleted: 0 })
  const [columnSizing, setColumnSizing] = useState({})
  const debounceRef = useRef(null)

  const { organization_slug } = useParams()
  const [organization, setOrganization] = useState(null)
  const [currentUser] = useAtom(userAtom)
  const isSuperAdmin = !!(currentUser?.is_admin && currentUser?.is_super_admin)

  const [assignEncircledAdminTarget, setAssignEncircledAdminTarget] = useState(null)
  const [editTarget, setEditTarget] = useState(null)
  const [editUserModalOpen, setEditUserModalOpen] = useState(false)
  const [managingOrgsUser, setManagingOrgsUser] = useState(null)

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [searchText])

  const loadUsers = useCallback((orgId) => {
    setLoading(true)
    const params = {
      page: pagination.pageIndex + 1,
      per_page: pagination.pageSize,
    }
    if (orgId) params.organization_id = orgId
    if (debouncedSearch) params.search = debouncedSearch
    if (sorting.length > 0) {
      params.sort_by  = sorting[0].id === "admin" ? "is_admin" : sorting[0].id
      params.sort_dir = sorting[0].desc ? "desc" : "asc"
    }

    api.getUsers(params)
      .then(res => {
        const data = res.data.data ?? res.data
        setUsers(data)
        setPageCount(res.data.last_page ?? 1)
        setTotal(res.data.total ?? data.length)
        setAllTotals({
          plansCreated: res.data.total_plans_created ?? 0,
          plansCompleted: res.data.total_plans_completed ?? 0,
        })
        setLoading(false)
      })
      .catch(() => {
        setUsers([])
        setLoading(false)
      })
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearch, sorting])

  useEffect(() => {
    if (organization_slug) {
      api.getOrganizationBySlug(organization_slug)
        .then(res => {
          setOrganization(res.data)
          loadUsers(res.data.id)
        })
        .catch(() => { setUsers([]); setLoading(false) })
    } else {
      loadUsers()
    }
  }, [organization_slug, loadUsers])

  const handleRemoveAdmin = useCallback((user) => {
    api.adminUpdateUser(user.id, { is_admin: false, is_super_admin: false })
      .then(() => loadUsers(organization?.id))
      .catch(console.error)
  }, [loadUsers, organization])

  // Tracked as a Set (not a single scalar) and keyed error map, so that a
  // second send (row B) started before the first (row A) resolves can never
  // clear row A's in-flight state or its error message. Each row's outcome
  // is independent of every other row's.
  const [invitingUserIds, setInvitingUserIds] = useState(() => new Set())
  const [actionErrors, setActionErrors] = useState({})
  const actionErrorRef = useRef(null)
  const prevErrorKeysRef = useRef(new Set())

  const handleSendInvitation = useCallback((user) => {
    setInvitingUserIds(prev => new Set(prev).add(user.id))
    setActionErrors(prev => {
      const next = { ...prev }
      delete next[user.id]
      return next
    })
    api.sendUserInvitation(user.id)
      .then(res => {
        const updated = res.data.user
        setUsers(prev => prev.map(u => (u.id === updated.id ? { ...u, ...updated } : u)))
      })
      .catch(err => {
        // The server refuses once the user has logged in, outside the resend
        // window, or for accounts it did not create. Its message is the useful
        // part, so surface it rather than swallowing it into console.error.
        // Name the user in the message since multiple rows' errors can be
        // shown at once.
        const message = err.response?.data?.message || "Failed to send the invitation."
        setActionErrors(prev => ({ ...prev, [user.id]: `${user.name || user.email}: ${message}` }))
      })
      .finally(() => {
        setInvitingUserIds(prev => {
          const next = new Set(prev)
          next.delete(user.id)
          return next
        })
      })
  }, [])

  // Bring newly-added errors into view without stealing focus from wherever
  // it currently is (e.g. the row's Actions button after the menu closes).
  useEffect(() => {
    const currentKeys = Object.keys(actionErrors)
    const hasNewError = currentKeys.some(k => !prevErrorKeysRef.current.has(k))
    prevErrorKeysRef.current = new Set(currentKeys)
    if (hasNewError) {
      actionErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [actionErrors])

  const isSuperAdminUser = (user) => user.is_admin && user.is_super_admin

  const columns = useMemo(() => [
    col.accessor("name", { header: "Name", size: 260, minSize: 100 }),
    col.accessor("email", { header: "Email", size: 220, minSize: 100 }),
    col.accessor(row => row.organizations?.map(o => o.name).join(', ') || "—", {
      id: "organization",
      header: "Organization",
      size: 180,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor("is_admin", {
      id: "admin",
      header: "Admin",
      size: 160,
      minSize: 70,
      cell: ({ row }) => {
        if (!row.original.is_admin) return <span className="text-gray-400">—</span>
        if (isSuperAdminUser(row.original)) {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-brandSecondary/10 text-brandSecondary">
              <i className="fa fa-shield-halved mr-1" />
              Encircled Admin
            </span>
          )
        }
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-brand/10 text-brand">
            <i className="fa fa-check mr-1" />
            Org Admin
          </span>
        )
      },
    }),
    col.accessor("plans_count", { header: "Plans Created", size: 120, minSize: 70, enableSorting: false }),
    col.accessor("completed_plans_count", { header: "Plans Completed", size: 140, minSize: 70, enableSorting: false }),
    ...(isSuperAdmin ? [col.accessor(row => getAccessStatus(row), {
      id: "access",
      header: "Access",
      size: 150,
      minSize: 90,
      enableSorting: false,
      cell: ({ row }) => <AccessStatusBadge status={getAccessStatus(row.original)} />,
    })] : []),
    ...(isSuperAdmin ? [col.display({
      id: "actions",
      header: "Actions",
      size: 120,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const u = row.original
        return (
          <Menu as="div" className="relative flex h-full items-center">
            <MenuButton className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand">
              Actions
              <i className="fa-solid fa-chevron-down text-[10px] text-gray-500" />
            </MenuButton>
            <MenuItems
              anchor="bottom end"
              className="z-50 w-48 rounded-md bg-white text-sm shadow-lg ring-1 ring-gray-200 focus:outline-none"
            >
              <div className="py-1">
                <MenuItem>
                  <button
                    onClick={() => setManagingOrgsUser(u)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-brand data-[focus]:bg-brand/10 text-left"
                  >
                    <i className="fa-solid fa-building w-4 text-brand" />
                    Manage Organizations
                  </button>
                </MenuItem>
                {!u.is_admin ? (
                  <MenuItem>
                    <button
                      onClick={() => setAssignEncircledAdminTarget(u)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-brandSecondary data-[focus]:bg-brandSecondary/10 text-left"
                    >
                      <i className="fa-solid fa-shield-halved w-4 text-brandSecondary" />
                      Make Encircled Admin
                    </button>
                  </MenuItem>
                ) : null}
                {u.is_admin ? (
                  <MenuItem>
                    <button
                      onClick={() => handleRemoveAdmin(u)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-red-600 data-[focus]:bg-red-50"
                    >
                      <i className="fa-solid fa-circle-xmark w-4 text-red-400" />
                      Remove Admin
                    </button>
                  </MenuItem>
                ) : null}
                {canSendInvitation(u) ? (
                  <MenuItem>
                    <button
                      onClick={() => handleSendInvitation(u)}
                      disabled={invitingUserIds.has(u.id)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-brand data-[focus]:bg-brand/10 text-left disabled:opacity-50"
                    >
                      <i className="fa-solid fa-envelope w-4 text-brand" />
                      {u.invitation_sent_at ? "Resend invitation" : "Send invitation email"}
                    </button>
                  </MenuItem>
                ) : null}
                <MenuItem>
                  <button
                    onClick={() => {
                      setEditTarget(u)
                      setEditUserModalOpen(true)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-gray-600 data-[focus]:bg-gray-50"
                  >
                    <i className="fa-solid fa-pen-to-square w-4 text-gray-400" />
                    Edit
                  </button>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        )
      },
    })] : []),
  ], [isSuperAdmin, handleRemoveAdmin, handleSendInvitation, invitingUserIds])

  const table = useReactTable({
    data: users,
    columns,
    pageCount,
    columnResizeMode: "onChange",
    state: { sorting, pagination, columnSizing },
    onSortingChange: updater => {
      setSorting(updater)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    },
    onPaginationChange: setPagination,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  const { pageIndex, pageSize } = pagination
  const from = total === 0 ? 0 : pageIndex * pageSize + 1
  const to = Math.min((pageIndex + 1) * pageSize, total)

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-brandSecondary leading-tight">
              {organization ? organization.name : "User Management"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {organization ? `Users for ${organization.name}` : "View and manage all users in the system"}
            </p>
          </div>
          {isSuperAdmin && (
            <button
              onClick={() => { setEditTarget(null); setEditUserModalOpen(true) }}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-opacity-90"
            >
              <i className="fa fa-plus mr-2"></i>
              Create User
            </button>
          )}
        </div>

        <div className="bg-white border border-gray-100 rounded-xl p-3 mb-3 shadow-sm">
          <div className="relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full bg-gray-50 border-0 rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
        </div>

        {Object.keys(actionErrors).length > 0 && (
          <div ref={actionErrorRef} role="alert" aria-live="polite" className="mb-3 flex flex-col gap-2">
            {Object.entries(actionErrors).map(([userId, message]) => (
              <div
                key={userId}
                className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                <i className="fa-solid fa-circle-exclamation mt-0.5" />
                <span className="flex-1">{message}</span>
                <button
                  onClick={() => setActionErrors(prev => {
                    const next = { ...prev }
                    delete next[userId]
                    return next
                  })}
                  className="text-red-400 hover:text-red-600"
                  aria-label="Dismiss error"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between items-center mb-2 px-1 text-xs text-gray-400">
          <span>
            {total === 0 ? "No users" : `Showing ${from}–${to} of ${total}`}
          </span>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="max-h-[600px] overflow-auto">
            <table
              className="border-separate border-spacing-0"
              style={{ width: table.getTotalSize(), minWidth: "100%", tableLayout: "fixed" }}
            >
              <thead>
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id} className="bg-gray-50">
                    {hg.headers.map(header => (
                      <th
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className="group relative sticky top-0 z-10 bg-gray-50 px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 select-none border-b border-gray-100"
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            <div
                              className={header.column.getCanSort() ? "flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors" : ""}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getCanSort() && (
                                <span className="text-gray-300">
                                  {{ asc: "↑", desc: "↓" }[header.column.getIsSorted()] ?? "↕"}
                                </span>
                              )}
                            </div>
                            {header.column.getCanResize() && (
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={e => e.stopPropagation()}
                                className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none ${header.column.getIsResizing() ? "bg-brand opacity-100" : "bg-gray-300 opacity-0 group-hover:opacity-100"}`}
                              />
                            )}
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-16 text-center text-gray-400 text-sm">
                      <i className="fa fa-spinner fa-spin mr-2" />Loading…
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-16 text-center">
                      <i className="fa-regular fa-folder-open text-3xl text-gray-200 mb-3 block" />
                      <p className="font-semibold text-gray-500 text-sm">No users found</p>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} style={{ width: cell.column.getSize() }} className="px-4 py-2 align-middle text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="flex items-center justify-between mt-4 px-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {pageIndex + 1} of {pageCount}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
            <select
              value={pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 outline-none focus:ring-2 focus:ring-brand/30"
            >
              {[10, 25, 50, 100].map(s => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-4 text-sm text-gray-500">
          Plans Created — Total: <strong className="text-gray-700">{allTotals.plansCreated}</strong>
          <span className="mx-3">·</span>
          Plans Completed — Total: <strong className="text-gray-700">{allTotals.plansCompleted}</strong>
        </div>
      </div>

      <AssignEncircledAdminModal
        isOpen={!!assignEncircledAdminTarget}
        onClose={() => setAssignEncircledAdminTarget(null)}
        onSuccess={() => { setAssignEncircledAdminTarget(null); loadUsers(organization?.id) }}
        user={assignEncircledAdminTarget}
      />
      <UserFormModal
        isOpen={editUserModalOpen}
        onClose={() => setEditUserModalOpen(false)}
        onSuccess={() => { setEditUserModalOpen(false); loadUsers(organization?.id) }}
        user={editTarget}
        defaultOrganizationId={organization?.id ?? ''}
      />
      {managingOrgsUser && (
        <ManageUserOrganizationsModal
          key={managingOrgsUser.id}
          isOpen={!!managingOrgsUser}
          onClose={() => setManagingOrgsUser(null)}
          onSuccess={() => loadUsers(organization?.id)}
          user={managingOrgsUser}
        />
      )}
    </AdminLayout>
  )
}

export default Users
